import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { ErrorState, LoadingSpinner } from '../../components/common';
import { migrationService, type ConnectionView } from '../../services/migration';

/**
 * The incumbent (LogicMonitor) connection. Credentials are WRITE-ONLY: typed
 * once, verified by the server, stored server-side, never shown again. The
 * write credential is optional and used only by the cutover.
 */
export function ConnectionPanel({ onChanged }: { onChanged?: () => void }) {
  const { tenantId } = useTenant();
  const [conn, setConn] = useState<ConnectionView | null | undefined>(undefined);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [company, setCompany] = useState('');
  const [accessId, setAccessId] = useState('');
  const [accessKey, setAccessKey] = useState('');
  const [wId, setWId] = useState('');
  const [wKey, setWKey] = useState('');
  const [showWrite, setShowWrite] = useState(false);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setError(null);
    try {
      setConn(await migrationService.getConnection(tenantId));
    } catch (err) {
      setError(err);
      setConn(null);
    }
  }, [tenantId]);
  useEffect(() => void load(), [load]);

  const run = async (fn: () => Promise<unknown>, after?: () => void) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      after?.();
      await load();
      onChanged?.();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const connect = (e: FormEvent) => {
    e.preventDefault();
    if (!tenantId) return;
    void run(
      () => migrationService.connect(tenantId, { company: company.trim(), accessId: accessId.trim(), accessKey }),
      () => { setEditing(false); setAccessId(''); setAccessKey(''); }
    );
  };
  const saveWrite = (e: FormEvent) => {
    e.preventDefault();
    if (!tenantId) return;
    void run(
      () => migrationService.setWriteCredential(tenantId, { accessId: wId.trim(), accessKey: wKey }),
      () => { setShowWrite(false); setWId(''); setWKey(''); }
    );
  };

  if (conn === undefined) return <LoadingSpinner />;

  const showForm = editing || conn === null;
  return (
    <section className="card" aria-label="Incumbent connection">
      <h2>Incumbent connection</h2>
      {error !== null && <ErrorState error={error} />}
      {conn && !showForm && (
        <>
          <dl className="detail-grid">
            <dt>Portal</dt>
            <dd>{conn.company}.logicmonitor.com</dd>
            <dt>Status</dt>
            <dd>
              <span className={`status-badge ${conn.status === 'verified' ? 'status-active' : 'status-down'}`}>{conn.status}</span>
              {conn.lastError && <span className="text-muted"> {conn.lastError}</span>}
            </dd>
            <dt>Last verified</dt>
            <dd>{conn.lastVerifiedAt ? new Date(conn.lastVerifiedAt).toLocaleString() : 'never'}</dd>
            <dt>Read credential</dt>
            <dd>{conn.credentialsOnFile ? 'on file (not shown)' : 'missing'}</dd>
            <dt>Write credential</dt>
            <dd>
              {conn.writeCredentialOnFile ? 'on file (not shown)' : 'none'} —{' '}
              {conn.capability.write === 'proven'
                ? 'proven by a cutover preflight'
                : conn.capability.write === 'denied'
                  ? 'REFUSED by LogicMonitor when tested'
                  : 'not yet tested (needed only for cutover)'}
            </dd>
          </dl>
          {conn.inventory && (
            <p className="text-muted">
              The portal holds{' '}
              {Object.entries(conn.inventory)
                .filter(([, n]) => n !== null)
                .map(([k, n]) => `${n} ${k}`)
                .join(', ')}
              .
            </p>
          )}
          <div className="page-actions">
            <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => tenantId && void run(() => migrationService.verify(tenantId))}>
              Verify again
            </button>
            <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => { setCompany(conn.company); setEditing(true); }}>
              Replace read credential
            </button>
            <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => setShowWrite((v) => !v)}>
              {conn.writeCredentialOnFile ? 'Replace write credential' : 'Add write credential'}
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              disabled={busy}
              onClick={() => {
                if (tenantId && window.confirm('Disconnect from LogicMonitor? The stored credentials stay in the secret store but are no longer used.')) {
                  void run(() => migrationService.disconnect(tenantId));
                }
              }}
            >
              Disconnect
            </button>
          </div>
        </>
      )}
      {showForm && (
        <form onSubmit={connect}>
          <p className="text-muted">
            Use a LogicMonitor API user with <strong>read-only</strong> access. The Access ID and Key are checked against
            LogicMonitor, stored securely on the server and never shown again.
          </p>
          <div className="form-field">
            <label htmlFor="mig-company">LogicMonitor portal</label>
            <input id="mig-company" name="company" required placeholder="acme (for acme.logicmonitor.com)" value={company} onChange={(e) => setCompany(e.target.value)} autoComplete="off" />
          </div>
          <div className="form-field">
            <label htmlFor="mig-id">Access ID</label>
            <input id="mig-id" name="accessId" required value={accessId} onChange={(e) => setAccessId(e.target.value)} autoComplete="off" />
          </div>
          <div className="form-field">
            <label htmlFor="mig-key">Access Key</label>
            <input id="mig-key" name="accessKey" type="password" required value={accessKey} onChange={(e) => setAccessKey(e.target.value)} autoComplete="new-password" />
          </div>
          <div className="page-actions">
            <button type="submit" className="btn btn-primary" disabled={busy}>Verify and connect</button>
            {conn && <button type="button" className="btn btn-ghost" onClick={() => setEditing(false)}>Cancel</button>}
          </div>
        </form>
      )}
      {showWrite && conn && (
        <form onSubmit={saveWrite} aria-label="Write credential">
          <h3>Write credential (cutover only)</h3>
          <p className="text-muted">
            A <strong>separate</strong> API user whose role may manage the devices being cut over. It is used only to switch the
            incumbent’s alerting off (and back on). Revoke it in LogicMonitor when the migration is finished.
          </p>
          <div className="form-field">
            <label htmlFor="mig-wid">Write Access ID</label>
            <input id="mig-wid" name="writeAccessId" required value={wId} onChange={(e) => setWId(e.target.value)} autoComplete="off" />
          </div>
          <div className="form-field">
            <label htmlFor="mig-wkey">Write Access Key</label>
            <input id="mig-wkey" name="writeAccessKey" type="password" required value={wKey} onChange={(e) => setWKey(e.target.value)} autoComplete="new-password" />
          </div>
          <button type="submit" className="btn btn-primary" disabled={busy}>Save write credential</button>
        </form>
      )}
    </section>
  );
}
